import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="compass"
export default class extends Controller {
  static values = {
    targetLat: Number,
    targetLng: Number,
  };

  static targets = ["arrow", "distance", "permissionOverlay"];

  connect() {
    this.currentLat = null;
    this.currentLng = null;
    this.bearing = 0;
    this.watchId = null;
  }

  disconnect() {
    if (this.watchId) navigator.geolocation.clearWatch(this.watchId);
    window.removeEventListener(
      "deviceorientationabsolute",
      this.handleOrientation.bind(this),
    );
    window.removeEventListener(
      "deviceorientation",
      this.handleOrientation.bind(this),
    );
  }

  async startHunt() {
    // 1. Request Gyroscope Permission (Critical for iOS 13+)
    if (
      typeof DeviceOrientationEvent !== "undefined" &&
      typeof DeviceOrientationEvent.requestPermission === "function"
    ) {
      try {
        const permission = await DeviceOrientationEvent.requestPermission();
        if (permission !== "granted") {
          alert("Compass access is required to use the locator.");
          return;
        }
      } catch (error) {
        console.error("Permission request failed", error);
      }
    }

    // Hide the overlay to reveal the compass
    this.permissionOverlayTarget.style.opacity = "0";
    setTimeout(() => {
      this.permissionOverlayTarget.classList.add("hidden");
    }, 500);

    // 2. Start tracking GPS
    this.startGPS();

    // 3. Start tracking Phone Rotation
    if ("ondeviceorientationabsolute" in window) {
      window.addEventListener(
        "deviceorientationabsolute",
        this.handleOrientation.bind(this),
        true,
      );
    } else {
      window.addEventListener(
        "deviceorientation",
        this.handleOrientation.bind(this),
        true,
      );
    }
  }

  startGPS() {
    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        this.currentLat = position.coords.latitude;
        this.currentLng = position.coords.longitude;

        this.updateDistanceAndBearing();
      },
      (error) => console.warn("GPS tracking error", error),
      { enableHighAccuracy: true, maximumAge: 0 },
    );
  }

  updateDistanceAndBearing() {
    if (!this.currentLat || !this.targetLatValue) return;

    // Calculate Distance
    const dist = this.calculateDistance(
      this.currentLat,
      this.currentLng,
      this.targetLatValue,
      this.targetLngValue,
    );
    this.distanceTarget.textContent = Math.round(dist);

    // Calculate Bearing (The true geographical angle to the shop)
    this.bearing = this.calculateBearing(
      this.currentLat,
      this.currentLng,
      this.targetLatValue,
      this.targetLngValue,
    );
  }

  handleOrientation(event) {
    if (this.bearing === 0) return; // Wait until GPS locks

    let compassHeading = null;

    // Apple Devices
    if (event.webkitCompassHeading) {
      compassHeading = event.webkitCompassHeading;
    }
    // Android Devices
    else if (event.absolute && event.alpha !== null) {
      compassHeading = 360 - event.alpha;
    }
    // Fallback
    else if (event.alpha !== null) {
      compassHeading = 360 - event.alpha;
    }

    if (compassHeading !== null) {
      // Calculate how much to rotate the CSS arrow so it points at the shop
      let arrowRotation = this.bearing - compassHeading;

      // CSS Transform
      this.arrowTarget.style.transform = `rotate(${arrowRotation}deg)`;
    }
  }

  // --- MATHEMATICAL HELPERS ---

  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371e3; // Earth radius in meters
    const radLat1 = (lat1 * Math.PI) / 180;
    const radLat2 = (lat2 * Math.PI) / 180;
    const deltaLat = ((lat2 - lat1) * Math.PI) / 180;
    const deltaLon = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(radLat1) *
        Math.cos(radLat2) *
        Math.sin(deltaLon / 2) *
        Math.sin(deltaLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Result in meters
  }

  calculateBearing(lat1, lon1, lat2, lon2) {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const toDeg = (rad) => (rad * 180) / Math.PI;

    const dLon = toRad(lon2 - lon1);
    const rLat1 = toRad(lat1);
    const rLat2 = toRad(lat2);

    const y = Math.sin(dLon) * Math.cos(rLat2);
    const x =
      Math.cos(rLat1) * Math.sin(rLat2) -
      Math.sin(rLat1) * Math.cos(rLat2) * Math.cos(dLon);
    let brng = toDeg(Math.atan2(y, x));

    // Normalize to 0-360
    return (brng + 360) % 360;
  }
}
